import { type ArtefactKind, ARTEFACT_KINDS } from './enums';

/**
 * What an artefact of each kind may be made of, and the two derivations a
 * console needs to put a file in front of a person as a kind
 * (docs/DECISIONS.md ZD-124 §3).
 *
 * ## One table, two consumers
 *
 * The store checks an uploader's declared kind against the bytes' media type
 * and refuses to disagree silently — *a check and not an inference*, because a
 * PDF uploaded as `text` would be indexed as binary noise that every citation
 * then points into. The console runs the same table the other way, to
 * pre-select the kind a file's media type admits so that a person confirms a
 * kind rather than being asked to know what `structured` means. Both read
 * this table, which lived in `@zevik/shared`'s store until the console needed
 * it and could not import that package. A second copy in the browser would be
 * the twin ZD-017 refuses, and the invisible kind: it would agree with the
 * store on every type both listed and disagree only on the one somebody
 * added to one of them.
 *
 * ## A browser does not always know a file's type
 *
 * `File.type` is the platform's extension map, and it is empty for `.md` and
 * `.yaml` on at least one platform. The store cannot accept a file with no
 * type, so `mediaTypeForFilename` is the fallback — a claim derived from the
 * file's *name*, made before any request and consulting nothing the record
 * knows, which is the line ZD-118 draws for what a page may decide alone. A
 * file with neither a reported type nor a known extension is refused on the
 * page rather than sent as `application/octet-stream` for the store to refuse
 * with less to say.
 */

export const ARTEFACT_MEDIA_TYPES: Readonly<Record<ArtefactKind, readonly string[]>> = {
  diagram_image: ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'],
  pdf: ['application/pdf'],
  text: ['text/plain', 'text/markdown', 'text/x-markdown'],
  structured: ['application/json', 'application/yaml', 'text/yaml'],
};

/** A media type without its parameters, lower-cased — the form the table holds. */
export function bareMediaType(mediaType: string): string {
  return mediaType.split(';')[0]!.trim().toLowerCase();
}

/**
 * The one kind whose media types include this one, or `null` where none
 * does. Never more than one: `artefact-media.test.ts` pins that no media
 * type appears under two kinds, because a type under two kinds would make
 * this a guess wearing a lookup.
 */
export function artefactKindForMediaType(mediaType: string): ArtefactKind | null {
  const bare = bareMediaType(mediaType);
  for (const kind of ARTEFACT_KINDS) {
    if (ARTEFACT_MEDIA_TYPES[kind].includes(bare)) return kind;
  }
  return null;
}

/**
 * Extensions a console may read a media type off when the platform reports
 * none. Every value here is in `ARTEFACT_MEDIA_TYPES`, pinned by test, so a
 * type derived this way is one the store accepts for some kind.
 */
export const MEDIA_TYPE_BY_EXTENSION: Readonly<Record<string, string>> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.webp': 'image/webp',
  '.svg': 'image/svg+xml',
  '.pdf': 'application/pdf',
  '.txt': 'text/plain',
  '.md': 'text/markdown',
  '.markdown': 'text/markdown',
  '.json': 'application/json',
  '.yaml': 'application/yaml',
  '.yml': 'application/yaml',
};

/** The media type a file's name admits, or `null` where the extension is not one the table knows. */
export function mediaTypeForFilename(filename: string): string | null {
  const dot = filename.lastIndexOf('.');
  if (dot < 0) return null;
  return MEDIA_TYPE_BY_EXTENSION[filename.slice(dot).toLowerCase()] ?? null;
}
